/**
 * ============================================================================
 * FHIR Client — Dual-Mode (EHR Launch + Standalone)
 * ============================================================================
 *
 * This module provides a unified FHIR API interface that works in both modes:
 *
 * 1. EHR Launch Mode:
 *    Uses fhirclient's authenticated Client instance. All requests go through
 *    client.request() which automatically handles token injection, refresh,
 *    and the correct FHIR server URL.
 *
 * 2. Standalone Mode:
 *    Uses a manual fetch wrapper with Keycloak JWT Bearer token injection.
 *    Requests go to the configured HAPI FHIR server with tenant routing.
 *
 * 3. Dev Bypass:
 *    Returns mock data (handled by the hooks layer, not here).
 *
 * Usage:
 *   const client = createFHIRClient({ authMode, fhirClient, token, tenantId });
 *   const patient = await client.get('Patient/123');
 * ============================================================================
 */

/**
 * Creates a unified FHIR client that works in both auth modes.
 *
 * @param {Object} options
 * @param {string} options.authMode - 'ehr' | 'standalone' | 'dev'
 * @param {Object} options.fhirClient - fhirclient Client instance (EHR mode only)
 * @param {string} options.token - Raw access token (standalone mode)
 * @param {string} options.tenantId - Tenant ID for standalone mode URL routing
 * @returns {Object} Unified FHIR client with get, post, put, patch, delete, search, batch methods
 */
export function createFHIRClient({ authMode, fhirClient, token, tenantId }) {
  if (authMode === 'ehr' && fhirClient) {
    return createEHRClient(fhirClient);
  }
  return createStandaloneClient(token, tenantId);
}

// ── EHR Launch Client (wraps fhirclient) ────────────────────────────────────

function createEHRClient(client) {
  return {
    /**
     * GET a FHIR resource.
     * fhirclient's client.request() handles auth, base URL, and token refresh.
     */
    get: async (resourcePath) => {
      return client.request(resourcePath);
    },

    /**
     * POST to create a new FHIR resource.
     */
    post: async (resourcePath, body) => {
      return client.request({
        url: resourcePath,
        method: 'POST',
        headers: { 'Content-Type': 'application/fhir+json' },
        body: JSON.stringify(body),
      });
    },

    /**
     * PUT to update an existing FHIR resource.
     */
    put: async (resourcePath, body) => {
      return client.request({
        url: resourcePath,
        method: 'PUT',
        headers: { 'Content-Type': 'application/fhir+json' },
        body: JSON.stringify(body),
      });
    },

    /**
     * PATCH to partially update a resource.
     */
    patch: async (resourcePath, body) => {
      return client.request({
        url: resourcePath,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json-patch+json' },
        body: JSON.stringify(body),
      });
    },

    /**
     * DELETE a FHIR resource.
     */
    delete: async (resourcePath) => {
      return client.request({
        url: resourcePath,
        method: 'DELETE',
      });
    },

    /**
     * Search for FHIR resources with query parameters.
     *
     * Array-valued params emit one `key=value` pair per array element
     * (FHIR's standard pattern for date-range intervals + AND-chained
     * searches: `date=ge2026-05-30&date=lt2026-05-31`).
     */
    search: async (resourceType, params = {}) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (Array.isArray(value)) {
          for (const v of value) {
            if (v !== null && v !== undefined) searchParams.append(key, v);
          }
        } else {
          searchParams.append(key, value);
        }
      });
      const query = searchParams.toString();
      const url = query ? `${resourceType}?${query}` : resourceType;
      return client.request(url);
    },

    /**
     * Batch/transaction operation.
     */
    batch: async (bundle) => {
      return client.request({
        url: '',
        method: 'POST',
        headers: { 'Content-Type': 'application/fhir+json' },
        body: JSON.stringify(bundle),
      });
    },

    /**
     * Get the FHIR server base URL.
     */
    getBaseURL: () => client.state?.serverUrl || '',
  };
}

// ── Standalone Client (manual fetch with Keycloak token) ────────────────────

function createStandaloneClient(token, tenantId) {
  const baseURL = import.meta.env.VITE_FHIR_BASE_URL || 'https://r4.collabkare.io';
  // CollabKare HAPI partitions are served under /{tenant}/data/{resource}
  // (matches KareApps + karePortal pattern; verified via Patient/-48 smoke 2026-05-28).
  // Without /data/ every FHIR call 404s.
  //
  // TV-P1-06 hardening: standalone mode is useless without a tenantId —
  // building `r4.collabkare.io/undefined/data/...` would silently 404
  // on every clinical view. Catch that at construction time with a
  // clear error rather than chasing 404s in the network tab.
  if (!tenantId) {
    const msg =
      '[FHIR] createStandaloneClient called without a tenantId. ' +
      'tenantId is set from the JWT during AuthProvider.init() via ' +
      'setTenantId(fhirTenantId) — check that the access token carries a ' +
      'fhirTenantId or patient_tenants claim and that AuthProvider ran ' +
      'before any hook tried to read FHIR data.';
    console.error(msg);
    throw new Error(msg);
  }
  const apiBaseURL = `${baseURL}/${tenantId}/data`;

  const buildURL = (resourcePath) => {
    const cleanPath = resourcePath.startsWith('/') ? resourcePath.slice(1) : resourcePath;
    return `${apiBaseURL}/${cleanPath}`;
  };

  const getFetchOptions = (method = 'GET', body = null, contentType = 'application/fhir+json') => {
    const options = {
      method,
      headers: {
        'Content-Type': contentType,
        'Accept': 'application/fhir+json',
        'Authorization': `Bearer ${token}`,
      },
    };
    if (body) {
      options.body = typeof body === 'string' ? body : JSON.stringify(body);
    }
    return options;
  };

  const request = async (url, options) => {
    try {
      const response = await fetch(url, options);

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorData;
        try {
          errorData = contentType?.includes('json')
            ? await response.json()
            : { message: await response.text() };
        } catch {
          errorData = { message: response.statusText };
        }

        const error = new Error(
          errorData?.issue?.[0]?.diagnostics ||
          errorData?.message ||
          `FHIR API error: ${response.status}`
        );
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      const ct = response.headers.get('content-type');
      return ct?.includes('json') ? response.json() : response;
    } catch (error) {
      console.error('[FHIR] Request failed:', { url, method: options.method, error: error.message });
      throw error;
    }
  };

  return {
    get: async (resourcePath) => request(buildURL(resourcePath), getFetchOptions('GET')),
    post: async (resourcePath, body) => request(buildURL(resourcePath), getFetchOptions('POST', body)),
    put: async (resourcePath, body) => request(buildURL(resourcePath), getFetchOptions('PUT', body)),
    patch: async (resourcePath, body) =>
      request(buildURL(resourcePath), getFetchOptions('PATCH', body, 'application/json-patch+json')),
    delete: async (resourcePath) => request(buildURL(resourcePath), getFetchOptions('DELETE')),
    search: async (resourceType, params = {}) => {
      const url = new URL(buildURL(resourceType));
      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (Array.isArray(value)) {
          for (const v of value) {
            if (v !== null && v !== undefined) url.searchParams.append(key, v);
          }
        } else {
          url.searchParams.append(key, value);
        }
      });
      return request(url.toString(), getFetchOptions('GET'));
    },
    batch: async (bundle) => request(buildURL(''), getFetchOptions('POST', bundle)),
    getBaseURL: () => apiBaseURL,
  };
}
